import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { doFetch, registerTeamsChannel } from '../services/api';
import { environment } from '../../environments/environment';
import { app } from '@microsoft/teams-js';
import { setWorkspaceTimezone } from '../../lib/cube-helper';

// Type definitions for Microsoft Teams context (matches API /teams/authorize body)
interface TeamsUser {
  id?: string;
  displayName?: string;
  licenseType?: string;
  loginHint?: string;
  userPrincipalName?: string;
  tenant?: {
    id?: string;
    teamsSku?: string;
  };
}

/** Microsoft Teams SDK context shape */
interface TeamsSDKContext {
  user?: TeamsUser;
  app?: {
    locale?: string;
    theme?: string;
  };
  channel?: {
    id?: string;
    displayName?: string;
  };
  team?: {
    id?: string;
    displayName?: string;
  };
}

/** From registerTeamsChannel successful response */
export interface AgentDetails {
  agentUserId: string;
  agentName: string;
  agentUUID: string;
  version?: string;
}

export function getAgentDetailKey(detail: AgentDetails): string {
  return `${detail.agentUUID}:${detail.version ?? ''}`;
}

export function getAgentDetailLabel(detail: AgentDetails): string {
  return detail.version ? `${detail.agentName} (${detail.version})` : detail.agentName;
}

interface UseTeamsContextResult {
  context: TeamsSDKContext | null;
  user: TeamsUser | null;
  loading: boolean;
  error: Error | null;
  initialized: boolean;
  /** JWT from /teams/authorize for Cube.js analytics API */
  cubeJsToken: string | null;
  /** JWT from /teams/authorize for REST API calls */
  authToken: string | null;
  /** App ID from /teams/authorize for API paths */
  appId: string | null;
  /** Tenant ID from /teams/authorize response (API may return misspelled key) */
  tenantId: string | null;
  /** Workspace timezone from /teams/authorize when provided */
  timezone: string | null;
  /** From registerTeamsChannel successful response */
  agentDetails: AgentDetails | null;
  /** All agents linked to the Teams channel */
  agentDetailsList: AgentDetails[];
}

interface TeamsAuthorizeResponse {
  status?: string;
  message?: string;
  cubeJsToken?: string;
  authToken?: string;
  appId?: string | number;
  tenantId?: string;
  tentantId?: string;
  timezone?: string | null;
}

const defaultContextValue: UseTeamsContextResult = {
  context: null,
  user: null,
  loading: false,
  error: null,
  initialized: false,
  cubeJsToken: null,
  authToken: null,
  appId: null,
  tenantId: null,
  timezone: null,
  agentDetails: null,
  agentDetailsList: [],
};

/** Mock Teams context for local development when isLocal is true */
const MOCK_TEAMS_CONTEXT = {"app":{"locale":"en-gb","sessionId":"d0b52826-1d51-47a6-a3ed-fbf7e86e3e1c","theme":"default","iconPositionVertical":-17,"osLocaleInfo":{"platform":"windows","regionalFormat":"en-GB","shortDate":"dd/MM/yyyy","longDate":"dd MMMM yyyy","shortTime":"HH:mm","longTime":"HH:mm:ss","timezone":"Asia/Calcutta"},"parentMessageId":"","userClickTime":1782829751125,"userClickTimeV2":0,"userFileOpenPreference":"inline","host":{"name":"TeamsModern","clientType":"web","features":{"nestedWildcardsInValidDomains":false,"serverSideRendering":false},"sessionId":"7f3293f3-544f-4cb7-8836-073db88a415d","ringId":"general","ancestors":["TeamsModern"]},"appLaunchId":"0da51581-d587-47eb-833d-b33333b846f4","appId":{"appIdAsString":"2ed0fb23-32b9-477d-9a94-c242a0b3e23c"},"manifestVersion":"1.22"},"page":{"id":"agentSummaryTab","frameContext":"content","subPageId":"","isFullScreen":false,"isMultiWindow":false,"isBackgroundLoad":false,"sourceOrigin":""},"user":{"id":"43593a73-fbda-44c5-9a62-ad385dbd105a","displayName":"","licenseType":"SmbNonVoice","loginHint":"akshay@TheLoops365.onmicrosoft.com","userPrincipalName":"akshay@TheLoops365.onmicrosoft.com","tenant":{"id":"2b09fb12-7295-4d4c-9ed9-8052e677bc03","teamsSku":"enterprise"}},"channel":{"id":"19:xjsw_8IUD6t_0IcEWdMSgG1xOclGWuMdkYoWE081FyY1@thread.tacv2","displayName":"Change Requests","relativeUrl":"/sites/ITOperations/Shared Documents/Change Requests","membershipType":"Regular","defaultOneNoteSectionId":""},"team":{"internalId":"19:xjsw_8IUD6t_0IcEWdMSgG1xOclGWuMdkYoWE081FyY1@thread.tacv2","displayName":"IT Operations","type":0,"groupId":"dc1c5e81-e31d-4475-87c4-fd1966d84065","isArchived":false,"userRole":0},"sharePointSite":{"teamSiteUrl":"https://theloops365.sharepoint.com/sites/ITOperations","teamSiteDomain":"theloops365.sharepoint.com","teamSitePath":"/sites/ITOperations","teamSiteId":""},"dialogParameters":{}} as TeamsSDKContext;
// const MOCK_TEAMS_CONTEXT = {"app":{"locale":"en-gb","sessionId":"bead5a36-593d-4bf2-8f00-7bfe5765e2ee","theme":"default","iconPositionVertical":-97,"osLocaleInfo":{"platform":"windows","regionalFormat":"en-GB","shortDate":"dd/MM/yyyy","longDate":"dd MMMM yyyy","shortTime":"HH:mm","longTime":"HH:mm:ss","timezone":"Asia/Calcutta"},"parentMessageId":"","userClickTime":1781678182656,"userClickTimeV2":0,"userFileOpenPreference":"inline","host":{"name":"TeamsModern","clientType":"web","features":{"nestedWildcardsInValidDomains":false,"serverSideRendering":false},"sessionId":"d850b39b-f281-448c-a8bf-e0a921c8bbc5","ringId":"general","ancestors":["TeamsModern"]},"appLaunchId":"495dcc61-c6de-4905-a638-5b694d45a60f","appId":{"appIdAsString":"2ed0fb23-32b9-477d-9a94-c242a0b3e23c"},"manifestVersion":"1.22"},"page":{"id":"agentSummaryTab","frameContext":"content","subPageId":"","isFullScreen":false,"isMultiWindow":false,"isBackgroundLoad":false,"sourceOrigin":""},"user":{"id":"43593a73-fbda-44c5-9a62-ad385dbd105a","displayName":"","licenseType":"SmbNonVoice","loginHint":"akshay@TheLoops365.onmicrosoft.com","userPrincipalName":"akshay@TheLoops365.onmicrosoft.com","tenant":{"id":"2b09fb12-7295-4d4c-9ed9-8052e677bc03","teamsSku":"enterprise"}},"channel":{"id":"19:Mxlw_w9eVVtoNXEuwGoy3yVEvun_UyPPoO-7OjPKpP41@thread.tacv2","displayName":"Tech Sandbox","relativeUrl":"/sites/TheLoopsSandbox/Shared Documents/Tech Sandbox","membershipType":"Regular","defaultOneNoteSectionId":""},"team":{"internalId":"19:Mxlw_w9eVVtoNXEuwGoy3yVEvun_UyPPoO-7OjPKpP41@thread.tacv2","displayName":"TheLoops Sandbox","type":0,"groupId":"dbdc7975-993e-406f-86c2-5e5a010ccfdf","isArchived":false,"userRole":0},"sharePointSite":{"teamSiteUrl":"https://theloops365.sharepoint.com/sites/TheLoopsSandbox","teamSiteDomain":"theloops365.sharepoint.com","teamSitePath":"/sites/TheLoopsSandbox","teamSiteId":""},"dialogParameters":{}} as TeamsSDKContext;

const TeamsContext = createContext<UseTeamsContextResult>(defaultContextValue);

/**
 * Provider that initializes Teams context and authorize flow; must wrap the app so useTeamsContext() has access to cubeJsToken and user.
 */
export function TeamsProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<TeamsSDKContext | null>(null);
  const [user, setUser] = useState<TeamsUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [cubeJsToken, setCubeJsToken] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [agentDetails, setAgentDetails] = useState<AgentDetails | null>(null);
  const [agentDetailsList, setAgentDetailsList] = useState<AgentDetails[]>([]);
  const getClientTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  };

  const applyAuthorizeResponse = (
    res: TeamsAuthorizeResponse,
    teamsContext: TeamsSDKContext,
    options?: { requireAgentOnChannel?: boolean }
  ) => {
    if (res.status !== 'ok') {
      setError(new Error(res.message ?? 'Teams authorize returned non-ok status'));
      return;
    }

    const cubeToken = res.cubeJsToken;
    const auth = res.authToken;
    const appIdFromRes = res.appId;
    const tenantFromRes = res.tenantId ?? res.tentantId;
    const tenantFromTeams = teamsContext.user?.tenant?.id;

    const resolvedTenantId = String(tenantFromRes ?? tenantFromTeams ?? '') || null;
    setTenantId(resolvedTenantId);
    if (resolvedTenantId) {
      localStorage.setItem('tenant', resolvedTenantId);
    }

    if (res.timezone) {
      setTimezone(res.timezone);
      setWorkspaceTimezone(res.timezone);
    } else {
      const clientTimezone = getClientTimezone();
      setTimezone(clientTimezone);
      setWorkspaceTimezone(clientTimezone);
    }

    if (cubeToken) {
      setCubeJsToken(cubeToken);
      localStorage.setItem('cubejsToken', cubeToken);
    } else {
      setError(new Error('Missing cubeJsToken from Teams authorize response'));
    }

    if (auth) {
      setAuthToken(auth);
      localStorage.setItem('authToken', auth);
      const tenantIdForChannel = teamsContext.user?.tenant?.id;
      const channelId = teamsContext.channel?.id;
      const teamId = (teamsContext as any)?.team?.groupId as string | undefined;
      if (tenantIdForChannel && channelId) {
        registerTeamsChannel(tenantIdForChannel, channelId, auth, teamId, environment.envName).then(
          (channelRes) => {
            if (channelRes == null) return;
            const wrapped = channelRes as {
              data?: {
                agentDetails?: Array<{
                  agentUserId?: string | number;
                  agentName?: string;
                  agentUUID?: string;
                  version?: string;
                }>;
              };
            };
            const agentDetailsArr = wrapped?.data?.agentDetails;
            if (Array.isArray(agentDetailsArr) && agentDetailsArr.length > 0) {
              const normalized = agentDetailsArr.map((item) => ({
                agentUserId: String(item.agentUserId ?? ''),
                agentName: item.agentName ?? '',
                agentUUID: item.agentUUID ?? '',
                version: item.version,
              }));
              setAgentDetailsList(normalized);
              setAgentDetails(normalized[0]);
            } else if (options?.requireAgentOnChannel) {
              setError(
                new Error(
                  'No agent is linked to this channel yet. Please register an agent to continue.'
                )
              );
            }
          },
          (err) => console.warn('Teams channel registration failed:', err)
        );
      }
    }

    if (appIdFromRes) {
      setAppId(String(appIdFromRes));
    }
  };

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Local development: use mocked Teams context (no SDK required)
        if (environment.isLocal) {
          console.log('isLocal: true - using mocked Teams context for local development');
          const teamsContext = MOCK_TEAMS_CONTEXT;
          setContext(teamsContext);
          setUser(teamsContext.user || null);
          setInitialized(true);
          setLoading(false);

          if (teamsContext) {
            doFetch('api/teams/authorize', {
              method: 'POST',
              body: teamsContext,
              skipAuth: true,
            })
              .then((result) => {
                if (result != null) {
                  console.log('Teams authorize success (mock):', result);
                  applyAuthorizeResponse(result as TeamsAuthorizeResponse, teamsContext);
                } else {
                  setError(new Error('Empty response from Teams authorize'));
                }
              })
              .catch((err) => {
                console.error('Teams authorize error:', err);
                const authError = err instanceof Error ? err : new Error(String(err));
                setError(authError);
              });
          }
          return;
        }

        // Use official Teams v2 SDK import instead of window.microsoftTeams
        await app.initialize();
        setInitialized(true);

        const teamsContext = await app.getContext();
        console.log('Full Teams Context:', teamsContext);
        console.log('User Info:', teamsContext.user);
        console.log('User JSON:', JSON.stringify(teamsContext.user, null, 2));

        setContext(teamsContext as unknown as TeamsSDKContext);
        setUser((teamsContext as any).user || null);
        setLoading(false);

        if (teamsContext) {
          doFetch('api/teams/authorize', {
            method: 'POST',
            body: teamsContext,
            skipAuth: true,
          })
            .then((result) => {
              if (result != null) {
                console.log('Teams authorize success:', result);
                applyAuthorizeResponse(result as TeamsAuthorizeResponse, teamsContext as TeamsSDKContext, {
                  requireAgentOnChannel: true,
                });
              } else {
                setError(new Error('Empty response from Teams authorize'));
              }
            })
            .catch((err) => {
              console.error('Teams authorize error:', err);
              const authError = err instanceof Error ? err : new Error(String(err));
              setError(authError);
            });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        const msg = (error.message || '').toLowerCase();
        const asString = String(err).toLowerCase();

        if (
          msg.includes('no parent window') ||
          msg.includes('initialization failed') ||
          msg.includes('not in teams') ||
          asString.includes('no parent window') ||
          asString.includes('initialization failed')
        ) {
          console.log('Not running in Microsoft Teams environment');
          setLoading(false);
          setInitialized(false);
          return;
        }

        console.error('Error initializing Teams context:', error);
        setError(error);
        setLoading(false);
        setInitialized(false);
      }
    };

    initializeTeams().catch((err) => {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Unhandled error initializing Teams context:', error);
      setError(error);
      setLoading(false);
      setInitialized(false);
    });
  }, []);

  // Store tokens in localStorage when available (for cube-helper and other consumers)
  useEffect(() => {
    if (cubeJsToken) {
      localStorage.setItem('cubejsToken', cubeJsToken);
    }
  }, [cubeJsToken]);
  useEffect(() => {
    if (authToken) {
      localStorage.setItem('authToken', authToken);
    }
  }, [authToken]);
  useEffect(() => {
    if (tenantId) {
      localStorage.setItem('tenant', tenantId);
    }
  }, [tenantId]);

  const value: UseTeamsContextResult = {
    context,
    user,
    loading,
    error,
    initialized,
    cubeJsToken,
    authToken,
    appId,
    tenantId,
    timezone,
    agentDetails,
    agentDetailsList,
  };

  return (
    <TeamsContext.Provider value={value}>
      {children}
    </TeamsContext.Provider>
  );
}

/**
 * React hook to access Microsoft Teams context, user, and cubeJsToken.
 * Must be used within TeamsProvider.
 *
 * @returns {UseTeamsContextResult} context, user, loading, error, initialized, cubeJsToken
 */
export function useTeamsContext(): UseTeamsContextResult {
  return useContext(TeamsContext);
}
